import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { srOnly } from '../../components/organic/motion';
import api from '../../utils/api';
import { errorText } from '../../utils/errorText';
import { COLOURS, MAX_LABEL, MAX_ROIS } from '../../utils/thermal/roi';
import { templateBody, templateProblem } from '../../utils/thermal/template';
import { box, button, input } from '../shared/styles';

/*
 * The experiment's ROI template (experiments.thermal_roi_template, 0096):
 * the labels, colours and reference every capture is measured with. The
 * Thermal Analysis Panel names each shape drawn after it, and opens a unit's
 * next capture with its shapes where they were on the unit's previous one.
 * Edited by experiment writers until the experiment is archived; shown to
 * every reader of the tab.
 *
 * The tab hands it a new `template` object each time it reloads its data.
 * Only a template whose CONTENT differs from what the editor was last filled
 * from refills it, and never over edits not yet saved: those are kept, and a
 * note offers the saved version instead (thermal-template-changed).
 */
const rowsOf = (template) => (template?.rois || []).map((r) => ({ label: r.label, colour: r.colour }));
const contentOf = (rows, reference) => JSON.stringify({ rows, reference: reference || null });
const templateContent = (template) => contentOf(rowsOf(template), template?.reference_label);

export default function ThermalTemplate({ experimentId, template, mayEdit, onSaved }) {
  const { t } = useTranslation('experiments');
  const [rows, setRows] = useState(rowsOf(template));
  const [reference, setReference] = useState(template?.reference_label || null);
  const [message, setMessage] = useState(null);
  const [changedElsewhere, setChangedElsewhere] = useState(false);
  // What the editor was last filled from; edits are unsaved while the draft differs from it.
  const seeded = useRef(templateContent(template));
  const dirty = useRef(false);
  dirty.current = contentOf(rows, reference) !== seeded.current;
  const fill = (next) => {
    seeded.current = templateContent(next);
    setRows(rowsOf(next));
    setReference(next?.reference_label || null);
    setChangedElsewhere(false);
  };
  useEffect(() => {
    const incoming = templateContent(template);
    if (incoming === seeded.current) return;          // the same template again: edits stay
    if (dirty.current) setChangedElsewhere(true);     // another version, over unsaved edits
    else fill(template);
  }, [template]);

  const problem = templateProblem(rows, reference);
  const change = (i, patch) => setRows((list) => list.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const save = async (body) => {
    setMessage(null);
    try {
      await api.put(`/experiments/${experimentId}/thermal-template`, { template: body });
      // What was saved is what the editor now holds: the reload that follows brings the same.
      fill(body);
      setMessage({ kind: 'ok', text: body ? t('thermal.template.saved') : t('thermal.template.cleared') });
      onSaved?.();
    } catch (e) {
      setMessage({ kind: 'error', text: errorText(e) });
    }
  };

  if (!mayEdit && !template) return null;
  return (
    <section style={box} aria-labelledby="thermal-template-title" data-testid="thermal-template">
      <h4 id="thermal-template-title" style={{ marginTop: 0 }}>{t('thermal.template.title')}</h4>
      <p style={{ fontSize: 13, marginTop: 0 }}>{t('thermal.template.intro')}</p>
      {!mayEdit ? (
        <ul style={{ margin: 0, paddingInlineStart: 18, fontSize: 13 }}>
          {rows.map((r) => (
            <li key={r.label}>
              <span aria-hidden="true" style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                background: r.colour, marginInlineEnd: 6 }} />
              {r.label}{r.label === reference ? ` · ${t('thermal.template.reference')}` : ''}
            </li>
          ))}
        </ul>
      ) : (
        <>
          <table style={{ fontSize: 13, borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'start' }}>{t('thermal.template.label')}</th>
                <th>{t('thermal.template.colour')}</th>
                <th>{t('thermal.template.reference')}</th>
                <th><span style={srOnly}>{t('thermal.template.remove')}</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i}>
                  <td>
                    <input style={{ ...input, width: 160 }} value={r.label} maxLength={MAX_LABEL}
                      aria-label={t('thermal.template.labelN', { n: i + 1 })}
                      onChange={(e) => change(i, { label: e.target.value })} data-testid={`thermal-template-label-${i}`} />
                  </td>
                  <td>
                    <input type="color" value={r.colour || COLOURS[i % COLOURS.length]}
                      aria-label={t('thermal.template.colourN', { n: i + 1 })}
                      onChange={(e) => change(i, { colour: e.target.value.toUpperCase() })}
                      data-testid={`thermal-template-colour-${i}`} />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input type="radio" name="thermal-template-reference" checked={!!r.label && r.label === reference}
                      aria-label={t('thermal.template.referenceN', { n: i + 1 })}
                      onChange={() => setReference(r.label)} data-testid={`thermal-template-reference-${i}`} />
                  </td>
                  <td>
                    <button type="button" onClick={() => {
                      if (r.label === reference) setReference(null);
                      setRows((list) => list.filter((_, j) => j !== i));
                    }} aria-label={t('thermal.template.removeN', { n: i + 1 })}
                    data-testid={`thermal-template-remove-${i}`}>×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
            <button type="button" disabled={rows.length >= MAX_ROIS}
              onClick={() => setRows((list) => [...list, { label: '', colour: COLOURS[list.length % COLOURS.length] }])}
              data-testid="thermal-template-add">{t('thermal.template.add')}</button>
            <button type="button" style={button('var(--c-primary)')} disabled={!!problem}
              onClick={() => save(templateBody(rows, reference))} data-testid="thermal-template-save">
              {t('thermal.template.save')}</button>
            {template && (
              <button type="button" onClick={() => save(null)} data-testid="thermal-template-clear">
                {t('thermal.template.clear')}</button>
            )}
          </div>
          {changedElsewhere && (
            <p role="status" style={{ fontSize: 13, margin: '6px 0 0' }} data-testid="thermal-template-changed">
              {t('thermal.template.changedElsewhere')}{' '}
              <button type="button" onClick={() => fill(template)} data-testid="thermal-template-reload">
                {t('thermal.template.reload')}</button>
            </p>
          )}
          {problem && rows.length > 0 && (
            <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '6px 0 0' }}
              data-testid="thermal-template-problem">{t(`thermal.template.problem.${problem}`, { max: MAX_ROIS })}</p>
          )}
        </>
      )}
      {message && (
        <p role={message.kind === 'ok' ? 'status' : 'alert'} data-testid="thermal-template-message"
          style={{ margin: '6px 0 0', fontSize: 13, color: message.kind === 'ok' ? undefined : 'var(--c-danger-fg)' }}>
          {message.text}</p>
      )}
    </section>
  );
}
